class ShippingOptions extends HTMLElement {
  constructor() {
    super();
    this.variantsData = [];
    this.freeShippingThreshold = 6500;
    this.selectedShippingOption = 'free';
    this.shippingInventoryData = {};
    this.init();
  }

  static get observedAttributes() {
    return ['data-variants', 'data-free-shipping-threshold'];
  }

  init() {
    const threshold = this.getAttribute('data-free-shipping-threshold');
    if (threshold) {
      this.freeShippingThreshold = parseInt(threshold);
    } else {
      // Default to $65.00 (6500 cents) if not set
      this.freeShippingThreshold = 6500;
    }

    this.loadVariantsData();
    this.setupEventListeners();
    this.render();
    
    setTimeout(() => {
      this.updateVariantClasses();
    }, 1000);
  }

  async loadVariantsData() {
    const sessionKey = 'shipping_variants_data';
    let cachedData = sessionStorage.getItem(sessionKey);
    
    if (cachedData) {
      try {
        this.variantsData = JSON.parse(cachedData);
        await this.loadShippingInventoryData();
      } catch (error) {
        sessionStorage.removeItem(sessionKey);
        await this.fetchVariantsData();
      }
    } else {
      await this.fetchVariantsData();
    }
  }

  async fetchVariantsData() {
    try {
      const variantScript = document.querySelector('script#variants_data');
      if (variantScript) {
        const variants = JSON.parse(variantScript.textContent);
        
        this.variantsData = variants.map(variant => ({
          id: variant.id,
          title: variant.title,
          price: variant.price,
          available: variant.available,
          barcode: variant.barcode,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3
        }));
        
        sessionStorage.setItem('shipping_variants_data', JSON.stringify(this.variantsData));
        await this.loadShippingInventoryData();
      }
    } catch (error) {
      // Silently handle error - variants data might not be available yet
      this.variantsData = [];
    }
  }

  async loadShippingInventoryData() {
    for (const variant of this.variantsData) {
      if (variant.barcode && variant.barcode.trim() !== '') {
        await this.fetchShippingInventory(variant.barcode, variant.id);
      }
    }
    this.updateVariantClasses();
  }

  async fetchShippingInventory(barcode, variantId) {
    const sessionKey = `shipping_inventory_${barcode}`;
    let cached = sessionStorage.getItem(sessionKey);
    
    if (cached) {
      try {
        const data = JSON.parse(cached);
        if (data && !data.error) {
          this.shippingInventoryData[variantId] = data;
          return data;
        } else {
          sessionStorage.removeItem(sessionKey);
        }
      } catch (e) {
        sessionStorage.removeItem(sessionKey);
      }
    }
    
    const url = `https://1rxihmve07.execute-api.us-east-2.amazonaws.com/prod/api/variant/inventory?barcode=${barcode}`;
    
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      sessionStorage.setItem(sessionKey, JSON.stringify(data));
      this.shippingInventoryData[variantId] = data;
      return data;
    } catch (error) {
      const defaultData = { shippingType: 'eco', error: error.message };
      this.shippingInventoryData[variantId] = defaultData;
      sessionStorage.setItem(sessionKey, JSON.stringify(defaultData));
      return defaultData;
    }
  }

  getShippingTypeForVariant(variantId) {
    const inventoryData = this.shippingInventoryData[variantId];
    
    if (!inventoryData) {
      return 'eco';
    }
    
    if (inventoryData.error) {
      return 'eco';
    }
    
    if (inventoryData && Array.isArray(inventoryData) && inventoryData.length > 0) {
      const hasMiramar = inventoryData.some(item => item.location_name === 'Miramar');
      if (hasMiramar) {
        return 'both';
      }
    }
    
    return 'eco';
  }

  setupEventListeners() {
    this.addEventListener('change', (event) => {
      if (event.target.name === 'shipping') {
        this.selectedShippingOption = event.target.value;
        this.updateVariantClasses();
        this.updateShippingCardStyles();
      }
    });

   
 
  }

  updateShippingCardStyles() {
    const allCards = this.querySelectorAll('.shipping-card');
    allCards.forEach(card => card.classList.remove('shipping-card--selected'));
    
    const selectedRadio = this.querySelector(`input[name="shipping"]:checked`);
    if (selectedRadio) {
      const selectedCard = selectedRadio.nextElementSibling;
      if (selectedCard && selectedCard.classList.contains('shipping-card')) {
        selectedCard.classList.add('shipping-card--selected');
      }
    }
  }

    updateVariantClasses() {
    const variantItems = document.querySelectorAll('.variantItem');
    let currentSelectedVariant = null;
    let availableVariants = [];
    
    variantItems.forEach((variantItem) => {
      // Remove all shipping-related classes
      variantItem.classList.remove('shipping-unavailable', 'shipping-available', 'free-shipping-eligible');
      
      const variantInput = variantItem.querySelector('input[type="radio"]');
      if (!variantInput || variantInput.classList.contains('v-soldout')) {
        return;
      }
      
      const variantId = parseInt(variantInput.getAttribute('data-var-id'));
      if (!variantId) return;
      
      const variantData = this.variantsData.find(v => v.id === variantId);
      if (!variantData) return;
      
      const variantPrice = variantData.price;
      const hasBarcode = variantData.barcode && variantData.barcode.trim() !== '';
      const hasShippingData = this.shippingInventoryData[variantId] && !this.shippingInventoryData[variantId].error;
      const isAvailable = hasBarcode && hasShippingData;
      
      if (!isAvailable) {
        variantItem.classList.add('shipping-unavailable');
        return;
      }
      
      const shippingType = this.getShippingTypeForVariant(variantId);
      
      // Add free shipping eligibility class
      if (variantPrice >= this.freeShippingThreshold) {
        variantItem.classList.add('free-shipping-eligible');
      }

      // Determine shipping availability based on selected option
      const isShippingAvailable = this.isVariantAvailableForShipping(variantPrice, shippingType);

      // Add appropriate class
      if (isShippingAvailable) {
        variantItem.classList.add('shipping-available');
        availableVariants.push(variantInput);
      } else {
        variantItem.classList.add('shipping-unavailable');
      }
    });
    
    // Find currently selected variant
    const checkedVariant = document.querySelector('.variantItem input[type="radio"]:checked');
    if (checkedVariant) {
      currentSelectedVariant = checkedVariant;
    }
    
    // Auto-select available variant if current selection is unavailable
    this.autoSelectAvailableVariant(currentSelectedVariant, availableVariants);
    
    // Update cart buttons based on availability
    this.updateCartButtons(availableVariants.length > 0);
  }

  isVariantAvailableForShipping(variantPrice, shippingType) {
    switch (this.selectedShippingOption) {
      case 'free':
        return variantPrice >= this.freeShippingThreshold && shippingType === 'both';
      case 'economy':
        return true;
      case 'expedited':
        return shippingType === 'both';
      default:
        return false;
    }
  }

  autoSelectAvailableVariant(currentSelectedVariant, availableVariants) {
    // If current selection is unavailable and we have available variants
    if (currentSelectedVariant && currentSelectedVariant.closest('.variantItem').classList.contains('shipping-unavailable') && availableVariants.length > 0) {
      
      // Just click the first available variant label
      const firstAvailableVariant = availableVariants[0];
      const firstAvailableLabel = firstAvailableVariant.nextElementSibling;
      
      if (firstAvailableLabel && firstAvailableLabel.tagName === 'LABEL') {
        firstAvailableLabel.click();
      }
    }
  }

  updateCartButtons(hasAvailableVariants) {
    // Find all add to cart buttons
    const addToCartButtons = document.querySelectorAll('button[name="add"], .product-form__submit, .btn--add-to-cart, [data-add-to-cart]');
    
    // Find all checkout buttons
    const checkoutButtons = document.querySelectorAll('.btn--checkout, [data-checkout], .cart__checkout-button, .product-form__checkout');
    
    const allButtons = [...addToCartButtons, ...checkoutButtons];
    
    allButtons.forEach(button => {
      if (hasAvailableVariants) {
        // Remove disabled class when variants are available
        button.classList.remove('shipping-unavailable');
      } else {
        // Add disabled class when no variants are available
        button.classList.add('shipping-unavailable');
      }
    });
  }

  render() {
    const settings = {
      shipping_section_title: this.getAttribute('data-section-title') || 'Shipping Options',
      free_shipping_title: this.getAttribute('data-free-shipping-title') || 'Free U.S. Shipping over $65 +',
      free_shipping_delivery: this.getAttribute('data-free-shipping-delivery') || '3 to 5 Business Days',
      free_shipping_price: this.getAttribute('data-free-shipping-price') || 'FREE',
      free_shipping_default: this.getAttribute('data-free-shipping-default') === 'true',
      economy_shipping_enabled: this.getAttribute('data-economy-shipping-enabled') === 'true',
      economy_shipping_title: this.getAttribute('data-economy-shipping-title') || 'Economy Shipping',
      economy_shipping_delivery: this.getAttribute('data-economy-shipping-delivery') || '3 to 5 Business Days',
      economy_shipping_price: this.getAttribute('data-economy-shipping-price') || '$7.50',
      expedited_shipping_enabled: this.getAttribute('data-expedited-shipping-enabled') === 'true',
      expedited_shipping_title: this.getAttribute('data-expedited-shipping-title') || 'Expedited Shipping',
      expedited_shipping_delivery: this.getAttribute('data-expedited-shipping-delivery') || '2 Business Days',
      expedited_shipping_price: this.getAttribute('data-expedited-shipping-price') || '$19.99',
      free_shipping_threshold: this.freeShippingThreshold
    };

    this.innerHTML = `
      <style>
      /* Shipping-related variant item classes */
        .variantItem.shipping-unavailable {
          opacity: 0.5;
          pointer-events: none;
        }

        .variantItem.shipping-unavailable label {
          cursor: not-allowed;
        }

        .variantItem.free-shipping-eligible {
          position: relative;
        }

        .variantItem.free-shipping-eligible::after {
          content: "🚚";
          position: absolute;
          top: 5px;
          right: 5px;
          font-size: 12px;
          background: #4CAF50;
          color: white;
          border-radius: 50%;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
        }

        .variantItem.shipping-available {
          opacity: 1;
          pointer-events: auto;
        } 
        .visually-hidden {
          position: absolute !important;
          width: 1px !important;
          height: 1px !important;
          padding: 0 !important;
          margin: -1px !important;
          overflow: hidden !important;
          clip: rect(0, 0, 0, 0) !important;
          white-space: nowrap !important;
          border: 0 !important;
        }

        .shipping-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .shipping-options {
          border: none;
          padding: 0;
          margin: 0;
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .shipping-option {
          flex: 1;
          min-width: 0;
          width: calc(33.333% - 4px);
        }

        .shipping-option input[type="radio"] {
          position: absolute;
          opacity: 0;
          width: 1px;
          height: 1px;
        }

        .shipping-card {
          padding: 14px;
          transition: all .15s ease;
          text-decoration: none;
          color: inherit;
          width: 100%;
          max-width: 164px;
          min-height: 127px;
          background: #FFFFFF 0% 0% no-repeat padding-box;
          border: 1px solid #CBCBCB;
          border-radius: 4px;
          opacity: 1;
          display: flex;
          justify-content: left;
          align-items: center;
          flex-wrap: wrap;
          cursor: pointer;
        }

        .shipping-card--selected,
        .shipping-option input[type="radio"]:checked + .shipping-card {
          border-color: #000000;
          border-width: 2px;
          background-color: #ffffff;
        }

        .shipping-card:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .shipping-card:hover {
          border-color: #d1d5db;
        }

        .shipping-icon {
          margin-bottom: 2px;
          color: #374151;
          display: flex;
          justify-content: flex-start;
        }

        .shipping-icon svg {
          max-width: 28px;
          height: auto;
        }

        .shipping-info {
          min-height: 55px;
        }

        .shipping-content-new {
          gap: 0px;
          text-align: left;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          justify-content: start;
          min-height: 70px;
        }

        .shipping-title {
          margin: 0;
          text-align: left;
          font: normal normal 600 13px / 16px var(--poppins-font);
          letter-spacing: 0px;
          color: #000000;
        }

        .shipping-delivery {
          text-align: left;
          font: normal normal normal 13px / 20px var(--poppins-font);
          letter-spacing: 0px;
          color: #000000;
          margin: 0px;
        }

        .shipping-price {
          text-align: left;
          font: normal normal 600 13px / 20px var(--poppins-font);
          letter-spacing: 0px;
          color: #000000;
          margin: 0px;
        }

        /* Cart button shipping unavailable styles */
        button.shipping-unavailable {
          opacity: 0.5 !important;
          pointer-events: none !important;
          cursor: not-allowed !important;
        }

        button.shipping-unavailable:hover {
          opacity: 0.5 !important;
        }

        @media(max-width: 1199px) {
          .shipping-title {
            font: 600 12px / 16px var(--poppins-font);
          }
          .shipping-delivery {
            font: 11px / 20px var(--poppins-font);
          }
        }

        @media(max-width: 1025px) {
          .shipping-options {
            gap: 15px;
            gap: 10px;
            margin: 20px 0px;
          }
          .shipping-option {
            flex: inherit;
            width: auto;
          }
          .shipping-container {
            margin-left: 0;
          }
        }

        @media(max-width: 575px) {
          .shipping-options {
            gap: 10px;
            margin: 12px 0px;
          }
          .shipping-card {
            margin-bottom: 0;
            padding: 10px;
          }
        }

        @media(max-width: 380px) {
          .shipping-options {
            gap: 8px;
          }
          .shipping-card {
            max-width: 138px;
          }
        }
      </style>

      <div class="shipping-container">
        <fieldset class="shipping-options" role="radiogroup" aria-labelledby="shipping-title">
          <legend id="shipping-title" class="visually-hidden">${settings.shipping_section_title}</legend>
          
          <div class="shipping-option">
            <input type="radio" id="free-shipping" name="shipping" data-price="${this.freeShippingThreshold}" value="free" ${settings.free_shipping_default ? 'checked' : ''} aria-describedby="free-shipping-desc">
            <label for="free-shipping" class="shipping-card ${settings.free_shipping_default ? 'shipping-card--selected' : ''}" tabindex="0">
              <div class="shipping-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24.184" height="17.552" viewBox="0 0 24.184 17.552">
                  <g id="Group_3184" data-name="Group 3184" transform="translate(-1146 -659)">
                      <g id="Group_3182" data-name="Group 3182" transform="translate(5 3.208)">
                      <g id="noun-cargo-7411189" transform="translate(1145 660.017)">
                          <path id="Path_1051" data-name="Path 1051" d="M6.053,32.253H3.824A.334.334,0,0,1,3.5,31.91V22.373a1.334,1.334,0,0,1,.362-.92,1.2,1.2,0,0,1,.871-.384H16.257a1.2,1.2,0,0,1,.871.383,1.336,1.336,0,0,1,.354.779h2.826a1.107,1.107,0,0,1,.818.362,1.652,1.652,0,0,1,.409.889L22,26.559h.354a1.192,1.192,0,0,1,.869.383,1.342,1.342,0,0,1,.363.921v4.047a.334.334,0,0,1-.324.343h-.817a2.429,2.429,0,0,1-.637,1.343,2.165,2.165,0,0,1-3.187,0l-.018-.021a2.438,2.438,0,0,1-.619-1.324H10.515A2.428,2.428,0,0,1,9.878,33.6a2.165,2.165,0,0,1-3.186,0,2.439,2.439,0,0,1-.639-1.344Zm-1.9-2.129h2.64a2.16,2.16,0,0,1,2.99,0H15.8a.343.343,0,0,1,0,.686h-5.52a2.47,2.47,0,0,1,.231.756H16.84V22.371a.64.64,0,0,0-.172-.437.569.569,0,0,0-.413-.182H4.731a.568.568,0,0,0-.413.18.635.635,0,0,0-.172.437v7.751Zm2.134.686H4.148v.756h1.9A2.491,2.491,0,0,1,6.282,30.81Zm3.153-.086-.029-.03A1.546,1.546,0,0,0,7.15,30.7l-.006.006a1.773,1.773,0,0,0,0,2.4,1.545,1.545,0,0,0,2.272,0,1.772,1.772,0,0,0,.017-2.387ZM21.818,31.91V31.9a1.755,1.755,0,0,0-.455-1.179l-.015-.015a1.543,1.543,0,0,0-2.271,0,1.745,1.745,0,0,0-.471,1.2v.009a1.752,1.752,0,0,0,.455,1.178l.015.015a1.543,1.543,0,0,0,2.271,0A1.744,1.744,0,0,0,21.818,31.91Zm.624-.343h.493v-3.7a.636.636,0,0,0-.17-.437.567.567,0,0,0-.413-.182H17.489v4.323h.493a2.429,2.429,0,0,1,.637-1.343,2.165,2.165,0,0,1,3.187,0l.018.021a2.438,2.438,0,0,1,.619,1.324Zm-4.954-8.649v3.641h3.854L20.9,23.59a.949.949,0,0,0-.229-.511.487.487,0,0,0-.361-.161H17.488Z" transform="translate(-3.5 -21.07)" stroke="#000" stroke-width="0.2"/>
                      </g>
                    </g>
                      <g id="Ellipse_253" data-name="Ellipse 253" transform="translate(1146 659)" fill="#fff" stroke="#000" stroke-width="0.8">
                      <circle cx="5" cy="5" r="5" stroke="none"/>
                      <circle cx="5" cy="5" r="4.6" fill="none"/>
                      </g>
                      <path id="Path_1053" data-name="Path 1053" d="M2954.292-2566.059l1.53,1.448,1.814-2.785" transform="translate(-1805 3230.188)" fill="none" stroke="#000" stroke-linecap="round" stroke-width="0.8"/>
                  </g>
                </svg>
              </div>
              <div class="shipping-content-new">
                <div class="shipping-info">
                  <h3 class="shipping-title">${settings.free_shipping_title}</h3>
                  <p class="shipping-delivery" id="free-shipping-desc">${settings.free_shipping_delivery}</p>
                </div>
                <p class="shipping-price">${settings.free_shipping_price}</p>
              </div>
            </label>
          </div>

          ${settings.economy_shipping_enabled ? `
          <div class="shipping-option">
            <input type="radio" id="economy-shipping" name="shipping" value="economy" ${!settings.free_shipping_default ? 'checked' : ''} aria-describedby="economy-shipping-desc">
            <label for="economy-shipping" class="shipping-card ${!settings.free_shipping_default ? 'shipping-card--selected' : ''}" tabindex="0">
              <div class="shipping-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="20.284" height="13.426" viewBox="0 0 20.284 13.426">
                  <g id="Group_3185" data-name="Group 3185" transform="translate(-1149.9 -663.125)">
                      <g id="Group_3182" data-name="Group 3182" transform="translate(5 3.208)">
                      <g id="noun-cargo-7411189" transform="translate(1145 660.017)">
                          <path id="Path_1051" data-name="Path 1051" d="M6.053,32.253H3.824A.334.334,0,0,1,3.5,31.91V22.373a1.334,1.334,0,0,1,.362-.92,1.2,1.2,0,0,1,.871-.384H16.257a1.2,1.2,0,0,1,.871.383,1.336,1.336,0,0,1,.354.779h2.826a1.107,1.107,0,0,1,.818.362,1.652,1.652,0,0,1,.409.889L22,26.559h.354a1.192,1.192,0,0,1,.869.383,1.342,1.342,0,0,1,.363.921v4.047a.334.334,0,0,1-.324.343h-.817a2.429,2.429,0,0,1-.637,1.343,2.165,2.165,0,0,1-3.187,0l-.018-.021a2.438,2.438,0,0,1-.619-1.324H10.515A2.428,2.428,0,0,1,9.878,33.6a2.165,2.165,0,0,1-3.186,0,2.439,2.439,0,0,1-.639-1.344Zm-1.9-2.129h2.64a2.16,2.16,0,0,1,2.99,0H15.8a.343.343,0,0,1,0,.686h-5.52a2.47,2.47,0,0,1,.231.756H16.84V22.371a.64.64,0,0,0-.172-.437.569.569,0,0,0-.413-.182H4.731a.568.568,0,0,0-.413.18.635.635,0,0,0-.172.437v7.751Zm2.134.686H4.148v.756h1.9A2.491,2.491,0,0,1,6.282,30.81Zm3.153-.086-.029-.03A1.546,1.546,0,0,0,7.15,30.7l-.006.006a1.773,1.773,0,0,0,0,2.4,1.545,1.545,0,0,0,2.272,0,1.772,1.772,0,0,0,.017-2.387ZM21.818,31.91V31.9a1.755,1.755,0,0,0-.455-1.179l-.015-.015a1.543,1.543,0,0,0-2.271,0,1.745,1.745,0,0,0-.471,1.2v.009a1.752,1.752,0,0,0,.455,1.178l.015.015a1.543,1.543,0,0,0,2.271,0A1.744,1.744,0,0,0,21.818,31.91Zm.624-.343h.493v-3.7a.636.636,0,0,0-.17-.437.567.567,0,0,0-.413-.182H17.489v4.323h.493a2.429,2.429,0,0,1,.637-1.343,2.165,2.165,0,0,1,3.187,0l.018.021a2.438,2.438,0,0,1,.619,1.324Zm-4.954-8.649v3.641h3.854L20.9,23.59a.949.949,0,0,0-.229-.511.487.487,0,0,0-.361-.161H17.488Z" transform="translate(-3.5 -21.07)" stroke="#000" stroke-width="0.2"/>
                      </g>
                    </g>
                  </g>
                </svg>
              </div>
              <div class="shipping-content-new">
                <div class="shipping-info">
                  <h3 class="shipping-title">${settings.economy_shipping_title}</h3>
                  <p class="shipping-delivery" id="economy-shipping-desc">${settings.economy_shipping_delivery}</p>
                </div>
                <p class="shipping-price">${settings.economy_shipping_price}</p>
              </div>
            </label>
          </div>
          ` : ''}

          ${settings.expedited_shipping_enabled ? `
          <div class="shipping-option">
            <input type="radio" id="expedited-shipping" name="shipping" value="expedited" aria-describedby="expedited-shipping-desc">
            <label for="expedited-shipping" class="shipping-card" tabindex="0">
              <div class="shipping-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28.41" height="15.642" viewBox="0 0 28.41 15.642">
                <g id="noun-shipping-7610172" transform="translate(-3.5 -24.398)">
                    <path id="Path_1052" data-name="Path 1052" d="M23.93,24.422l7.635,2.556a.459.459,0,0,1,.345.444v9.594a.459.459,0,0,1-.313.433l-7.666,2.567a.447.447,0,0,1-.29,0l-7.633-2.555a.458.458,0,0,1-.346-.445V27.422a.459.459,0,0,1,.345-.444l7.635-2.556a.447.447,0,0,1,.29,0Zm-6.6,14.239a.458.458,0,1,1,0,.916H11.569a.458.458,0,1,1,0-.916Zm-3.069-10a.458.458,0,0,1,0,.916H8.993a.458.458,0,0,1,0-.916Zm0,3.335a.458.458,0,1,1,0,.916H3.958a.458.458,0,1,1,0-.916Zm0,3.335a.458.458,0,0,1,0,.916H6.637a.458.458,0,0,1,0-.916Zm6.075.286V33.719a.46.46,0,0,1,.381-.452l-.84-1.53-.805.957.185.058a.457.457,0,0,1,.32.437v2.185l.759.24Zm.916-1.27v1.895a.471.471,0,0,1-.021.138.457.457,0,0,1-.574.3L19,36.152a.459.459,0,0,1-.34-.443V33.524l-.517-.163a.507.507,0,0,1-.156-.087.457.457,0,0,1-.055-.643l1.653-1.964a.465.465,0,0,1,.15-.134.457.457,0,0,1,.62.18l1.666,3.033a.457.457,0,0,1-.529.673l-.241-.076Zm5.128-5.7a.422.422,0,0,1,.066-.021l1.765-.591a.371.371,0,0,1,.064-.021l1.741-.582L23.786,25.34l-6.227,2.085,6.227,2.085,2.591-.868Zm.636.752v3.055l.914-.305V29.087Zm1.831-.613v3.69a.459.459,0,0,1-.313.433l-1.8.6a.464.464,0,0,1-.178.036.458.458,0,0,1-.458-.458V29.7l-1.852.619v8.631l6.749-2.259V28.058l-2.15.72Zm-5.515,10.17V30.318l-6.749-2.259V36.69Z" transform="translate(0)"/>
                  </g>
                </svg>
              </div>
              <div class="shipping-content-new">
                <div class="shipping-info">
                  <h3 class="shipping-title">${settings.expedited_shipping_title}</h3>
                  <p class="shipping-delivery" id="expedited-shipping-desc">${settings.expedited_shipping_delivery}</p>
                </div>
                <p class="shipping-price">${settings.expedited_shipping_price}</p>
              </div>
            </label>
          </div>
          ` : ''}
        </fieldset>
      </div>
    `;

    this.selectedShippingOption = settings.free_shipping_default ? 'free' : 'economy';
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-variants' && oldValue !== newValue) {
      this.loadVariantsData();
    }
    
    if (name === 'data-free-shipping-threshold' && oldValue !== newValue) {
      this.freeShippingThreshold = parseInt(newValue, 10);
      this.updateVariantClasses();
    }
  }

  refreshVariantClasses() {
    this.updateVariantClasses();
  }

  forceAutoSelect() {
    this.updateVariantClasses();
  }

  clearSessionStorage() {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.startsWith('shipping_variants_data') || key.startsWith('shipping_inventory_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
    });
  }

  getSelectedShippingOption() {
    return this.selectedShippingOption;
  }

  getVariantsData() {
    return this.variantsData;
  }

  isVariantEligibleForFreeShipping(variantPrice) {
    return variantPrice >= this.freeShippingThreshold;
  }

  getShippingInventoryData() {
    return this.shippingInventoryData;
  }
}

customElements.define('shipping-options', ShippingOptions);

 

 